/**
 * CMS runtime diagnostics.
 *
 * ── ONE SHAPE, SO A PROBLEM IS FINDABLE ──────────────────────────────────────────────
 *
 * Every CMS runtime event goes through here with the same fields: what happened, which
 * block, which node, which version, which entity, and whether it was critical. Without
 * that, "the event page looked wrong" is unanswerable — with it, the console says exactly
 * which node of which document failed on which event.
 *
 * ── WHAT IS DELIBERATELY NOT LOGGED ──────────────────────────────────────────────────
 *
 * No document JSON: a whole block tree in a log line is unreadable and the version tables
 * already hold the content. No stack traces to the public console beyond the message. No
 * customer, order, buyer or ticket data — a rendering failure is not a reason to print
 * somebody's purchase into a log.
 *
 * Counters are kept in-process for the browser contract tests to read. There is no
 * telemetry platform here and this task does not add one.
 */

export type CmsDiagnosticEvent =
  | 'cms_resolve_failed'
  | 'cms_document_invalid_at_runtime'
  | 'cms_critical_block_failed'
  | 'cms_optional_block_failed'
  | 'cms_fallback_used'
  // A GlobalSection block whose referenced section is missing, archived, unpublished or
  // unrenderable. Reported rather than thrown: the block renders nothing and the page —
  // including its ticket panel — carries on.
  | 'cms_global_section_unresolved'
  | 'cms_global_section_block_failed';

export interface CmsDiagnosticContext {
  entityKind?: string;
  entityId?: string;
  component?: string;
  nodeId?: string;
  componentVersion?: number;
  expectedVersion?: number;
  critical?: boolean;
  reason?: string;
  message?: string;
  /** Which reusable section was involved, for the two global-section events. */
  sectionId?: string;
}

const counters: Record<string, number> = Object.create(null);

/** Read by the contract tests to assert that the right thing was reported. */
export function cmsDiagnosticCounters(): Record<string, number> {
  return { ...counters };
}

export function resetCmsDiagnostics(): void {
  for (const key of Object.keys(counters)) delete counters[key];
}

export function reportCms(event: CmsDiagnosticEvent, context: CmsDiagnosticContext = {}): void {
  counters[event] = (counters[event] ?? 0) + 1;

  // Bounded, flat, and free of anything that is not about rendering.
  const detail = {
    event,
    entity: context.entityKind && context.entityId
      ? `${context.entityKind}:${context.entityId}` : undefined,
    component: context.component,
    node: context.nodeId,
    version: context.componentVersion,
    expected: context.expectedVersion,
    critical: context.critical,
    reason: context.reason,
    message: context.message ? String(context.message).slice(0, 200) : undefined,
  };

  // A critical failure is an error; everything else is a warning. A page that still sells
  // tickets should not page somebody at three in the morning.
  const line = `[cms] ${event}`;
  if (event === 'cms_critical_block_failed') console.error(line, detail);
  else console.warn(line, detail);

  // Exposed for the browser contract tests, which cannot read a console.
  if (typeof window !== 'undefined') {
    const w = window as unknown as { __cmsDiagnostics?: unknown[] };
    w.__cmsDiagnostics = [...(w.__cmsDiagnostics ?? []), detail].slice(-50);
  }
}
